import { AnimatedSprite, Sprite, Text, Texture } from 'pixi.js';
import { FSM, Store, TextureFactory } from '../../core';
import { IEventEmitter, Point, StatefulAnimation, StateMap, Transitionable, Vector } from '../../interface';
import { BaseObject } from '../';
import { PowerProgressBar } from '../../ui';
import PlayerTextureInfo from './Player.json';
import { sound } from '@pixi/sound';
interface IPlayer extends IEventEmitter {
  hp: number;
  score: number;
  velocity: Vector;
  position: Point;
}

class Player extends BaseObject implements Transitionable {
  private fsm: FSM<IPlayer>;
  public hp: number;
  public score: number;
  public force: Vector;
  public canJump: boolean;
  private statefulAnimation: StatefulAnimation;
  public animatedSprite: AnimatedSprite;
  public invincibilityDT: number;

  private maxDT: number = 150;
  private arrow: Sprite;
  private radian: number = -Math.PI / 2;
  private radius: number = 130;
  private rotateDT: number = 0;
  private process: number = 0;
  private power: number = 0;
  private powerDT: number = 0;
  private progress: PowerProgressBar;
  public isSuperJump: boolean = false;
  public showUI: boolean = true;

  constructor(showUI: boolean = true, name?: string) {
    super();
    this.hp = Store.instance.state.hp;
    this.score = Store.instance.state.score;
    this.force = { x: 0, y: 0 };
    this.canJump = true;
    this.invincibilityDT = 0;
    this.showUI = showUI;

    const textures = TextureFactory.instance.load(PlayerTextureInfo);
    this.statefulAnimation = {
      IDLE: {
        textures: textures.slice(158, 173),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      DIE: {
        textures: textures.slice(0, 56),
        animationSpeed: 0.4,
        offset: { x: 0, y: 60 },
        loop: false,
      },
      DIE2: {
        textures: textures.slice(57, 126),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      WALK: {
        textures: textures.slice(239, 254),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      RUN: {
        textures: textures.slice(224, 238),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      JUMP_UP: {
        textures: textures.slice(180, 195),
        animationSpeed: 0.5,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      JUMP_DOWN: {
        textures: textures.slice(196, 204),
        animationSpeed: 0.5,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      CLEAR: {
        textures: textures.slice(127, 157),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
    };

    this.animatedSprite = new AnimatedSprite([Texture.EMPTY]);
    this.animatedSprite.updateAnchor = true;
    this.animatedSprite.onComplete = () => this.fsm.trigger('ANIMATION_DONE');
    this.fsm = new FSM<IPlayer>(this, this.states, 'IDLE');
    this.stage.addChild(this.animatedSprite);

    const texture = TextureFactory.instance.getSingleTexture('/textures/etc/arrow.png');
    this.arrow = new Sprite(texture);
    this.arrow.scale.set(0.7);
    this.arrow.anchor.set(0, 0.5);
    this.arrow.visible = showUI;

    this.ui.addChild(this.arrow);

    const nameTag = new Text(name || '', {
      fontSize: 42,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      stroke: '0xffffff',
      strokeThickness: 8,
      padding: 10,
    });
    nameTag.anchor.set(0.5);
    nameTag.position.set(0, -255);
    this.ui.addChild(nameTag);

    this.progress = new PowerProgressBar(0, 100);
    this.progress.alpha = 0;
    this.progress.y = 40;
    this.progress.scale.set(2);
    this.progress.visible = showUI;
    this.ui.addChild(this.progress);
  }

  public jump(): void {
    this.canJump = false;
    this.isSuperJump = false;
  }

  public next(): void {
    if (this.process < 1) {
      sound.play('/audio/jump.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.process = 2; // ++
      const power = 350;
      const x =
        (3 * Math.abs(Math.cos(this.radian))) / Math.cos(this.radian) + ((Math.cos(this.radian) * power) / 100) * 6;
      const y = -Math.sqrt(30 - ((Math.sin(this.radian) * power) / 100) * 260);

      this.velocity.x = x;
      this.velocity.y = y;

      if (this.velocity.x < 0) {
        this.animatedSprite.scale.x = Math.abs(this.animatedSprite.scale.x) * -1;
      } else if (this.velocity.x > 0) {
        this.animatedSprite.scale.x = Math.abs(this.animatedSprite.scale.x) * 1;
      }
      this.emit('jump');
    }
    // ! Legacy...
    //  else if (this.process === 1) {
    //   // JUMP!!!...
    //   const x =
    //     (3 * Math.abs(Math.cos(this.radian))) / Math.cos(this.radian) +
    //     ((Math.cos(this.radian) * this.power) / 100) * 6;
    //   const y = -Math.sqrt(30 - ((Math.sin(this.radian) * this.power) / 100) * 260);

    //   this.velocity.x = x;
    //   this.velocity.y = y;

    //   if (this.velocity.x < 0) {
    //     this.animatedSprite.scale.x = Math.abs(this.animatedSprite.scale.x) * -1;
    //   } else if (this.velocity.x > 0) {
    //     this.animatedSprite.scale.x = Math.abs(this.animatedSprite.scale.x) * 1;
    //   }

    //   this.process++;
    // }
  }

  public trigger(...events: Array<string>): void {
    this.fsm.trigger(...events);
  }

  public get state(): string {
    return this.fsm.state || '';
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    if (this.process === 0) {
      this.rotateDT += elapsed;
      this.rotateDT %= this.maxDT;
      this.powerDT = 0;
    }

    if (this.process <= 1) {
      if (this.arrow.alpha < 1 - 0.1 * elapsed) {
        this.arrow.alpha += 0.1 * elapsed;
      } else {
        this.arrow.alpha = 1;
      }
    } else {
      if (this.arrow.alpha > 0.1 * elapsed) {
        this.arrow.alpha -= 0.1 * elapsed;
      } else {
        this.arrow.alpha = 0;
      }
    }

    if (this.process === 1) {
      this.powerDT += elapsed;
      this.powerDT %= this.maxDT;

      if (this.progress.alpha < 1 - 0.1 * elapsed) {
        this.progress.alpha += 0.1 * elapsed;
      } else {
        this.progress.alpha = 1;
      }
    } else {
      if (this.progress.alpha > 0.1 * elapsed) {
        this.progress.alpha -= 0.1 * elapsed;
      } else {
        this.progress.alpha = 0;
      }
    }

    if (this.rotateDT > this.maxDT / 2) {
      this.radian = ((this.maxDT - this.rotateDT) / (this.maxDT / 2)) * (Math.PI / 180) * -120 - (Math.PI / 180) * 30;
    } else {
      this.radian = (this.rotateDT / (this.maxDT / 2)) * (Math.PI / 180) * -120 - (Math.PI / 180) * 30;
    }
    this.arrow.x = Math.cos(this.radian) * this.radius;
    this.arrow.y = Math.sin(this.radian) * this.radius - 50;
    this.arrow.rotation = this.radian;

    if (this.powerDT > this.maxDT / 2) {
      this.power = ((this.maxDT - this.powerDT) / (this.maxDT / 2)) * 400;
    } else {
      this.power = (this.powerDT / (this.maxDT / 2)) * 400;
    }

    this.progress.update(this.power, 400);

    if (this.invincibilityDT > 0) {
      this.animatedSprite.alpha = Math.floor(this.invincibilityDT / 5) % 2 === 0 ? 1 : 0.5;
      this.invincibilityDT -= elapsed;
    } else {
      this.invincibilityDT = 0;
      this.animatedSprite.alpha = 1;
    }

    this.fsm.update();
  }

  public hit(damage: number): void {
    if (this.invincibilityDT === 0 && !this.isSuperJump) {
      this.hp -= damage;

      if (this.hp <= 0) {
        this.hp = 0;
        this.process = 3;

        if (Math.random() < 0.5) {
          this.fsm.trigger('DIE');
        } else {
          this.fsm.trigger('DIE2');
        }
        this.emit('DIE');
      }

      this.vibrator.vibrate(12);
      this.invincibilityDT = 180;
      this.emit('hit', damage);
    }
  }

  private get states(): StateMap<IPlayer> {
    return {
      IDLE: {
        DIE: [{ events: ['DIE'] }],
        DIE2: [{ events: ['DIE2'] }],
        CLEAR: [{ events: ['CLEAR'] }],
        JUMP_UP: [[{ refs: ['velocity', 'y'] }, '<', 0]],
      },
      JUMP_UP: {
        DIE: [{ events: ['DIE'] }],
        DIE2: [{ events: ['DIE2'] }],
        CLEAR: [{ events: ['CLEAR'] }],
        JUMP_DOWN: [[{ refs: ['velocity', 'y'] }, '===', 0], '&&', { events: ['LANDING'] }],
      },
      JUMP_DOWN: {
        DIE: [{ events: ['DIE'] }],
        DIE2: [{ events: ['DIE2'] }],
        CLEAR: [{ events: ['CLEAR'] }],
        IDLE: [[{ refs: ['velocity', 'y'] }, '===', 0], '&&', { events: ['ANIMATION_DONE'] }],
        JUMP_UP: [[{ refs: ['velocity', 'y'] }, '<', 0]],
      },
      DIE: {},
      DIE2: {},
      CLEAR: {},
    };
  }

  public transition(prevState: string, nextState: string): void {
    const info = this.statefulAnimation[nextState];
    const flipX = this.animatedSprite.scale.x < 0 ? -1 : 1;

    this.animatedSprite.textures = info.textures;
    this.animatedSprite.x = info.offset.x * flipX;
    this.animatedSprite.y = info.offset.y;
    this.animatedSprite.scale.set(flipX * Math.abs(this.animatedSprite.scale.x), this.animatedSprite.scale.y);
    this.animatedSprite.animationSpeed = info.animationSpeed;
    this.animatedSprite.loop = info.loop;

    this.animatedSprite.gotoAndPlay(0);

    if (nextState === 'JUMP_DOWN' && this.hp > 0) {
      this.canJump = true;
      this.process = 0;
    } else if (nextState === 'CLEAR') {
      this.process = 3;
    }
  }
}

export default Player;
